import {
  BaseProductType, ProductRuleExclusiveCategory,
  ProductRuleIncompatibleGrids,
  ProductRuleMaxGangNo,
  ProductRuleRequiresOtherMech,
  ProductRuleSwitchOnly, ProductRuleTypeExclusiveCategory,
  ProductRuleTypeIncompatibleGrids,
  ProductRuleTypeMaxGangNo,
  ProductRuleTypeRequiresOtherMech,
  ProductRuleTypeSwitchOnly,
} from '../types/productTypes'
import { InflatedConfiguredInterfaceProduct } from '../list/ListTypes'
import { getInterfaceDisplayName } from './productUtils'
import { ProductDataShape } from '../ProductDataShape'

export interface ValidationMessage {
  readonly debugMessage: string
  readonly message: string
  readonly itemNo?: string
  readonly interfaceIndex?: number
}

function validateMaxGangNo(
  itemNo: string | null,
  displayName: string,
  interfaceIndex: number,
  rule: ProductRuleMaxGangNo,
  gangNo: number
): ValidationMessage | null {
  if (itemNo && rule.products.includes(itemNo) && gangNo > rule.maxGangNumber) {
    return {
      itemNo: itemNo,
      debugMessage: `Max gang no. for ${itemNo} is ${rule.maxGangNumber}, this is ${gangNo}`,
      message: `${displayName} can only be used in up to ${rule.maxGangNumber} gang switches`,
      interfaceIndex,
    }
  }

  return null
}

function validateOtherMech(
  itemNo: string | null,
  displayName: string,
  interfaceIndex: number,
  rule: ProductRuleRequiresOtherMech,
  selection: readonly InflatedConfiguredInterfaceProduct[],
  usedIndices: number[],
): ValidationMessage | null {
  if (itemNo && rule.products.includes(itemNo)) {
    const otherMechIndex = selection.findIndex((other, i) => {
      if (usedIndices.includes(i)) {
        return false
      }

      return other.mechProduct?.itemNo && rule.otherMechs.includes(other.mechProduct?.itemNo)
    })

    if (otherMechIndex >= 0) {
      usedIndices.push(otherMechIndex)
    } else {
      return {
        itemNo: itemNo,
        debugMessage: `An additional mech (${rule.otherMechs.toString()}) is required`,
        message: `Each ${displayName} needs to be paired with a switch`,
        interfaceIndex,
      }
    }
  }

  return null
}

function validateSwitchOnly(
  itemNo: string | null,
  displayName: string,
  interfaceIndex: number,
  rule: ProductRuleSwitchOnly,
  productType: BaseProductType,
): ValidationMessage | null {
  if (itemNo && rule.products.includes(itemNo) && productType !== 'Switch') {
    return {
      itemNo: itemNo,
      debugMessage: 'Only allowed in a switch',
      message: `${displayName} is only allowed in a switch`,
      interfaceIndex,
    }
  }

  return null
}

function validateIncompatibleGrids(
  itemNo: string | null,
  displayName: string,
  interfaceIndex: number,
  rule: ProductRuleIncompatibleGrids,
  gridItemNo: string | null
): ValidationMessage | null {
  if (itemNo && gridItemNo && rule.products.includes(itemNo) && rule.incompatibleGrids.includes(gridItemNo)) {
    return {
      itemNo: itemNo,
      debugMessage: `Not allowed in grid ${gridItemNo}`,
      message: `${displayName} can't be used with this product`,
      interfaceIndex,
    }
  }

  return null
}

interface InflatedInterfaceWithCategories {
  item: InflatedConfiguredInterfaceProduct
  category: string
  subcategory: string | null
}

function validateExclusiveCategory(
  category: string,
  subcategory: string | null,
  displayName: string,
  interfaceIndex: number,
  rule: ProductRuleExclusiveCategory,
  selection: readonly InflatedInterfaceWithCategories[],
): ValidationMessage | null {
  if (category === rule.category && (rule.subcategory === null || subcategory === rule.subcategory)) {
    const firstProductInDifferentCategory = selection.find((otherItem, otherIndex) => {
      if (otherIndex === interfaceIndex) {
        return false
      }

      if (otherItem.category !== rule.category) {
        return true
      }

      if (rule.subcategory === null) {
        return false
      }

      return otherItem.subcategory !== rule.subcategory
    })

    if (firstProductInDifferentCategory) {
      const categoryError: ValidationMessage = {
        itemNo: selection[interfaceIndex].item.interfaceProduct.itemNo,
        debugMessage: `${displayName} can only be used with other ${rule.subcategory ?? rule.category} items.`,
        message: `${displayName} can only be used with other ${rule.subcategory ?? rule.category} items.`,
        interfaceIndex: interfaceIndex,
      }

      return categoryError
    }
  }

  return null
}

export function validateMechs(Products: ProductDataShape, selection: readonly InflatedConfiguredInterfaceProduct[], productType: BaseProductType, gridItemNo: string | null): readonly ValidationMessage[] {
  const gangNo = selection.length
  const messages: ValidationMessage[] = []
  const usedIndices: number[] = []

  const selectionWithCategories: readonly InflatedInterfaceWithCategories[] = selection.map(item => {
    let category: string
    let subcategory: string | null

    if (item.mechProduct) {
      category = item.mechProduct.category
      subcategory = item.mechProduct.subcategory
    } else {
      category = item.interfaceProduct.category
      subcategory = item.interfaceProduct.subcategory
    }

    return {
      item,
      category,
      subcategory,
    }
  })

  selectionWithCategories.forEach(({ item, category, subcategory }, interfaceIndex) => {

    const interfaceItemNo = item.interfaceProduct.itemNo
    const mechItemNo = item.mechProduct?.itemNo ?? null

    const displayName = getInterfaceDisplayName(item.interfaceProduct, item.mechProduct)

    Products.rules.forEach(rule => {
      switch (rule.ruleType) {
        case ProductRuleTypeMaxGangNo:
          const maxGangError = validateMaxGangNo(mechItemNo, displayName, interfaceIndex, rule, gangNo)
            ?? validateMaxGangNo(interfaceItemNo, displayName, interfaceIndex, rule, gangNo)

          if (maxGangError) {
            messages.push(maxGangError)
          }

          break

        case ProductRuleTypeRequiresOtherMech:
          const otherMechError = validateOtherMech(mechItemNo, displayName, interfaceIndex, rule, selection, usedIndices)
            ?? validateOtherMech(interfaceItemNo, displayName, interfaceIndex, rule, selection, usedIndices)

          if (otherMechError) {
            messages.push(otherMechError)
          }

          break
        case ProductRuleTypeSwitchOnly:
          const switchOnlyError = validateSwitchOnly(mechItemNo, displayName, interfaceIndex, rule, productType)
            ?? validateSwitchOnly(interfaceItemNo, displayName, interfaceIndex, rule, productType)

          if (switchOnlyError) {
            messages.push(switchOnlyError)
          }

          break
        case ProductRuleTypeIncompatibleGrids:
          const incompatibleGridError = validateIncompatibleGrids(mechItemNo, displayName, interfaceIndex, rule, gridItemNo)
            ?? validateIncompatibleGrids(interfaceItemNo, displayName, interfaceIndex, rule, gridItemNo)

          if (incompatibleGridError) {
            messages.push(incompatibleGridError)
          }

          break
        case ProductRuleTypeExclusiveCategory:
          const exclusiveCategoryError = validateExclusiveCategory(category, subcategory, displayName, interfaceIndex, rule, selectionWithCategories)

          if (exclusiveCategoryError) {
            messages.push(exclusiveCategoryError)
          }

          break
      }
    })
  })

  return messages
}
